import { VilcFilesWorkflow } from "./vilc-opportunities-files-workflow";
import { VilcRequestsWorkflow } from "./vilc-opportunities-requests-workflow";
import { VilcRowsWorkflow } from "./vilc-opportunities-rows-workflow";
import type { VilcWorkflowDependencies } from "./vilc-opportunities-workflow-dependencies";
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { StateService } from "../../services/state.service";
import { HttpService } from "../../services/http.service";
import { MessageService } from "primeng/api";
import { HandleTaskService } from "../../services/handle-task.service";

export interface VILCOpportunity {
  sku: string;
  sku_detail: string;
  ptc_segment: number;
  brand: string;
  maco_hl: number;
  maco_hl_threshold: number;
  maco_hl_segment: number;
  vilc_hl: number;
  vilc_index: number;
  volume: number;
  financially_viable: boolean | null;
  have_new_value: boolean | null;
  new_vilc_hl?: number | null;
}

@Component({
  selector: "app-vilc-opportunities",
  templateUrl: "./vilc-opportunities.component.html",
  styleUrls: ["./vilc-opportunities.component.scss"],
  standalone: false,
})
export class VILCOpportunitiesComponent
  implements OnInit, OnChanges, OnDestroy
{
  private readonly vilcOpportunitiesFilesWorkflow = new VilcFilesWorkflow();
  private readonly vilcOpportunitiesRequestsWorkflow =
    new VilcRequestsWorkflow();
  private readonly vilcOpportunitiesRowsWorkflow = new VilcRowsWorkflow();
  private get workflowDependencies(): VilcWorkflowDependencies {
    const component = this;
    return {
      get stateService() {
        return component.stateService;
      },
      get httpService() {
        return component.httpService;
      },
      get messageService() {
        return component.messageService;
      },
    };
  }
  @Input() opportunities: VILCOpportunity[] = [];
  @Input() expId: string = "";
  @Output() actionButtonCall = new EventEmitter<any>();
  @Output() refreshVilcOpportunities = new EventEmitter<any>();
  rows: VILCOpportunity[] = [];
  formMap: Record<number, FormGroup> = {};
  pageSizes: number[] = [5, 10, 20, 50];
  pageSize: number = 5;
  currentPage: number = 1;
  totalItems: number = 0;
  showVilcGuideDialog: boolean = true;
  showModifyDialog: boolean = false;
  currentStep = 1;
  allowedFileType: any = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv",
  ];
  fileUploadContent: any = {
    title: "",
    fileName: "",
    fileSize: "",
    fileUploadPercent: 0,
    fileUploadStatus: false,
    disableFlag: false,
    loaderFlag: false,
    allowedFileType: this.allowedFileType,
    fileUploadedFlag: false,
    templateUrl: `templates/global_index_template.xlsx`,
    downloadSpinner: false,
    downloadText: "Download Template",
    fileUploadErrorMessage: "",
  };
  uploadedFile!: any;
  kbFactor = 1e3;
  mbFactor = 1e6;
  gbFactor = 1e9;
  buttonDisabled: boolean = true;
  showDialogLoader: boolean = false;
  excelHeader: any = [
    {
      label: "sku",
      value: "sku",
    },
    {
      label: "sku_detail",
      value: "sku_detail",
    },
    {
      label: "ptc_segment",
      value: "ptc_segment",
    },
    {
      label: "brand",
      value: "brand",
    },
    {
      label: "volume",
      value: "volume",
    },
    {
      label: "maco_hl",
      value: "maco_hl",
    },
    {
      label: "maco_hl_threshold",
      value: "maco_hl_threshold",
    },
    {
      label: "maco_hl_segment",
      value: "maco_hl_segment",
    },
    {
      label: "vilc_hl",
      value: "vilc_hl",
    },
    {
      label: "vilc_index",
      value: "vilc_index",
    },
    {
      label: "financially_viable",
      value: "financially_viable",
    },
    {
      label: "have_new_vilc_hl",
      value: "have_new_value",
    },
    {
      label: "new_vilc_hl",
      value: "new_vilc_hl",
    },
  ];
  validationData: any = {};
  showValidationError: boolean = false;
  private readonly sseControllers = new Set<AbortController>();

  constructor(
    private stateService: StateService,
    private httpService: HttpService,
    private messageService: MessageService,
    private handleTaskService: HandleTaskService,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.sseControllers.forEach((ctrl) => ctrl.abort());
    this.sseControllers.clear();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["opportunities"]) {
      this.loadData();
    }
  }

  loadData(): void {
    return this.vilcOpportunitiesRowsWorkflow.loadData(
      this,
      this.workflowDependencies,
    );
  }

  prepareForms(): void {
    return this.vilcOpportunitiesRowsWorkflow.prepareForms(
      this,
      this.workflowDependencies,
    );
  }

  setViable(row: VILCOpportunity, value: boolean): void {
    return this.vilcOpportunitiesRowsWorkflow.setViable(
      this,
      this.workflowDependencies,
      row,
      value,
    );
  }

  startEdit(row: VILCOpportunity): void {
    return this.vilcOpportunitiesRowsWorkflow.startEdit(
      this,
      this.workflowDependencies,
      row,
    );
  }

  sethave_new_value(row: VILCOpportunity, value: boolean | null): void {
    return this.vilcOpportunitiesRowsWorkflow.sethave_new_value(
      this,
      this.workflowDependencies,
      row,
      value,
    );
  }

  // ...existing code...
  onnew_vilc_hlChange(row: VILCOpportunity, value: any): void {
    return this.vilcOpportunitiesRowsWorkflow.onnew_vilc_hlChange(
      this,
      this.workflowDependencies,
      row,
      value,
    );
  }
  // ...existing code...
  trackByIndex(index: number, _item: VILCOpportunity) {
    return index;
  }

  get totalPages(): number {
    return this.vilcOpportunitiesRowsWorkflow.totalPages(
      this,
      this.workflowDependencies,
    );
  }

  get startIndex(): number {
    return this.vilcOpportunitiesRowsWorkflow.startIndex(
      this,
      this.workflowDependencies,
    );
  }

  get endIndex(): number {
    return this.vilcOpportunitiesRowsWorkflow.endIndex(
      this,
      this.workflowDependencies,
    );
  }

  get paginatedRows(): VILCOpportunity[] {
    return this.vilcOpportunitiesRowsWorkflow.paginatedRows(
      this,
      this.workflowDependencies,
    );
  }

  onPageSizeChange(event: any): void {
    return this.vilcOpportunitiesRowsWorkflow.onPageSizeChange(
      this,
      this.workflowDependencies,
      event,
    );
  }

  prevPage(): void {
    return this.vilcOpportunitiesRowsWorkflow.prevPage(
      this,
      this.workflowDependencies,
    );
  }

  nextPage(): void {
    return this.vilcOpportunitiesRowsWorkflow.nextPage(
      this,
      this.workflowDependencies,
    );
  }

  onPageInputChange(event: Event): void {
    return this.vilcOpportunitiesRowsWorkflow.onPageInputChange(
      this,
      this.workflowDependencies,
      event,
    );
  }

  openVilcGuideDialog() {
    this.showVilcGuideDialog = true;
  }

  nextStep() {
    if (this.currentStep < 3) {
      this.currentStep++;
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  finish() {
    this.showVilcGuideDialog = false;
    this.currentStep = 1;
  }

  openModifyDialog() {
    this.showModifyDialog = true;
  }

  parseFileSize(sizeInBytes: any): string {
    return this.vilcOpportunitiesFilesWorkflow.parseFileSize(
      this,
      this.workflowDependencies,
      sizeInBytes,
    );
  }

  onDownloadResults(): void {
    return this.vilcOpportunitiesFilesWorkflow.onDownloadResults(
      this,
      this.workflowDependencies,
    );
  }

  onFileDrop(event: any): void {
    return this.vilcOpportunitiesFilesWorkflow.onFileDrop(
      this,
      this.workflowDependencies,
      event,
    );
  }

  onFileBrowse(e: any): void {
    return this.vilcOpportunitiesFilesWorkflow.onFileBrowse(
      this,
      this.workflowDependencies,
      e,
    );
  }

  onFileUploadCancel(_event: any = ""): void {
    return this.vilcOpportunitiesFilesWorkflow.onFileUploadCancel(
      this,
      this.workflowDependencies,
      _event,
    );
  }

  onFileUploadApiCall(): void {
    return this.vilcOpportunitiesFilesWorkflow.onFileUploadApiCall(
      this,
      this.workflowDependencies,
    );
  }

  // Function for Common API Call
  commonFileApiCall(
    apiUrl: string,
    method: string,
    params: any,
    callback: (response: any, params: any) => void,
  ): void {
    return this.vilcOpportunitiesRequestsWorkflow.commonFileApiCall(
      this,
      this.workflowDependencies,
      apiUrl,
      method,
      params,
      callback,
    );
  }
  runValidationAPI(method: string): void {
    return this.vilcOpportunitiesRequestsWorkflow.runValidationAPI(
      this,
      this.workflowDependencies,
      method,
    );
  }
  // Function for check the file type validation
  fileTypeValidation(): boolean {
    return this.vilcOpportunitiesFilesWorkflow.fileTypeValidation(
      this,
      this.workflowDependencies,
    );
  }
  onDialogClose() {
    if (this.showModifyDialog) {
      this.showModifyDialog = false;
    }
  }

  editModifyProceed(): void {
    return this.vilcOpportunitiesRequestsWorkflow.editModifyProceed(
      this,
      this.workflowDependencies,
    );
  }

  onSimulate(): void {
    return this.vilcOpportunitiesRowsWorkflow.onSimulate(
      this,
      this.workflowDependencies,
    );
  }

  get isSimulationDisabled(): boolean {
    return this.vilcOpportunitiesRowsWorkflow.isSimulationDisabled(
      this,
      this.workflowDependencies,
    );
  }

  // Function for Common API Call
  commonApiCall(
    apiUrl: string,
    method: string,
    params: any,
    callback: (response: any, params: any) => void,
    extraFlag: any = {},
  ): void {
    return this.vilcOpportunitiesRequestsWorkflow.commonApiCall(
      this,
      this.workflowDependencies,
      apiUrl,
      method,
      params,
      callback,
      extraFlag,
    );
  }

  // Function for polling(status) api call
  pollingApiCall(taskId: any) {
    const ctrl = this.handleTaskService.streamTaskStatus(
      taskId,
      () => {
        this.runValidationAPI("getMethod");
      },
      () => {
        this.messageService.add({
          key: "w",
          severity: "warn",
          summary: "Something went wrong!",
          detail: "Task Failed",
          sticky: true,
        });
      },
      () => {},
    );
    this.sseControllers.add(ctrl);
  }

  restrictToNumeric(event: KeyboardEvent, currentValue: string = ""): void {
    return this.vilcOpportunitiesRowsWorkflow.restrictToNumeric(
      this,
      this.workflowDependencies,
      event,
      currentValue,
    );
  }
}
